import type { ReactNode } from "react";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { InterviewStatusBadge } from "../../components/interviewList/InterviewStatusBadge";
import {
  formatInterviewDate,
  formatInterviewTime,
} from "../../components/interviewList/schedule";
import { resolveInterviewDisplayStatus } from "../../components/interviewList/status";
import { useAdminInterviewDetailGraphQL } from "../../hooks/useAdminSummaryCheck";
import { logger } from "../../lib/logger";

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex gap-4 border-[var(--color-border-default)] border-b py-3 last:border-b-0">
      <div className="w-40 shrink-0 font-medium text-[13px] text-[var(--color-text-secondary)]">
        {label}
      </div>
      <div className="text-[14px] text-[var(--color-text-primary)]">
        {children}
      </div>
    </div>
  );
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <div className="mb-6 overflow-hidden rounded-xl border border-[var(--color-border-default)] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
      <div className="border-[var(--color-primary-500)] border-b-2 bg-[var(--color-bg-subtle-gray)] px-6 py-5">
        <h2 className="m-0 font-bold text-2xl text-[var(--color-primary-500)]">
          {title}
        </h2>
        {description && (
          <p className="mt-1 mb-0 text-[13px] text-[var(--color-text-secondary)]">
            {description}
          </p>
        )}
      </div>
      <div className="px-6 py-4">{children}</div>
    </div>
  );
}

const actionButtonClass =
  "cursor-pointer rounded-md border border-[var(--color-primary-500)] bg-white px-3 py-1 font-medium text-[13px] text-[var(--color-primary-500)] hover:bg-[var(--color-primary-50)]";

/**
 * ステークホルダーインタビューの詳細。
 *
 * 基本情報と録画・文字起こしをまとめて見る画面。要約を作らないため要約チェック
 * 画面に辿り着けず、録画と文字起こしはここでしか取り出せない。
 */
export function StakeholderInterviewDetail() {
  const { interviewId } = useParams<{ interviewId: string }>();
  const { interview, loading, error } = useAdminInterviewDetailGraphQL(
    interviewId ?? null,
  );
  const [copied, setCopied] = useState(false);

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-200px)] bg-[var(--color-bg-subtle-gray)] p-6">
        <div className="p-10 text-center text-[var(--color-text-secondary)]">
          読み込み中...
        </div>
      </div>
    );
  }

  if (error || !interview) {
    return (
      <div className="min-h-[calc(100vh-200px)] bg-[var(--color-bg-subtle-gray)] p-6">
        <div className="p-10 text-center text-[#ef4444]">
          {error ?? "インタビューが見つかりません"}
        </div>
      </div>
    );
  }

  const transcript = interview.rawVttText;

  const handleCopy = async () => {
    if (!transcript) return;
    try {
      await navigator.clipboard.writeText(transcript);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (caught) {
      logger.error(caught as Error);
      alert("コピーに失敗しました");
    }
  };

  const handleDownload = () => {
    if (!transcript) return;
    // 保存しているのは Zoom から届いた VTT そのもの。読む用に整形したものは残していない
    const url = URL.createObjectURL(
      new Blob([transcript], { type: "text/vtt" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = `${interview.interviewId}.vtt`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-[calc(100vh-200px)] bg-[var(--color-bg-subtle-gray)] p-6">
      <div className="w-full">
        <div className="mb-6 flex items-center gap-3">
          <h1 className="m-0 font-bold text-2xl text-[var(--color-text-primary)]">
            {interview.candidateName} さま インタビュー
          </h1>
          <InterviewStatusBadge
            status={resolveInterviewDisplayStatus(interview)}
          />
        </div>

        <Section title="基本情報">
          <Row label="回答者">
            {interview.candidateName}
            {interview.candidateEmail && (
              <span className="ml-2 text-[13px] text-[var(--color-text-secondary)]">
                {interview.candidateEmail}
              </span>
            )}
          </Row>
          {interview.stakeholderContext && (
            <>
              <Row label="受け取る人">
                {interview.stakeholderContext.recipientNames.join("、")}
              </Row>
              <Row label="使った設定">
                {interview.stakeholderContext.stakeholderSettingName}
              </Row>
            </>
          )}
          <Row label="インタビュアー">{interview.interviewerName ?? "-"}</Row>
          <Row label="実施日時">
            {formatInterviewDate(interview.startTime)}{" "}
            {formatInterviewTime(interview.startTime)} -{" "}
            {formatInterviewTime(interview.endTime)}
          </Row>
          <Row label="スポンサー">{interview.sponsorName ?? "-"}</Row>
          <Row label="インタビュー">{interview.termTitle ?? "-"}</Row>
        </Section>

        <Section
          title="録画・文字起こし"
          description="Zoom の録画完了通知を受け取って自動で紐づきます"
        >
          <Row label="録画">
            {interview.zoomRecordingViewUrl ? (
              <a
                href={interview.zoomRecordingViewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--color-primary-500)] underline"
              >
                Zoom クラウド録画を開く
              </a>
            ) : (
              <span className="text-[var(--color-text-secondary)]">
                まだ届いていません
              </span>
            )}
          </Row>
          <Row label="文字起こし">
            {transcript ? (
              <div className="flex items-center gap-3">
                <span>取得済み</span>
                <button
                  type="button"
                  onClick={handleCopy}
                  className={actionButtonClass}
                >
                  {copied ? "コピーしました" : "VTTをコピー"}
                </button>
                <button
                  type="button"
                  onClick={handleDownload}
                  className={actionButtonClass}
                >
                  VTTをダウンロード
                </button>
              </div>
            ) : (
              <span className="text-[var(--color-text-secondary)]">
                まだ届いていません
              </span>
            )}
          </Row>
        </Section>
      </div>
    </div>
  );
}
